import $ from 'jquery';
import React, { useEffect } from 'react';
import classnames from 'classnames';
import RefineResultsButton from '../common/RefineResultsButton';
import ReactTableWrapper from '../ReactTableWrapper';
import SectionPagination from '../SectionPagination';
import BaseInvoiceListRefineResults from './BaseInvoiceListRefineResults';
import useManualReactTableState from '../ManualReactTableState';
import {
    convertDataToListingDisplay,
    convertInvoiceListingToAReadableFormat,
    getGraphQLTotalRecords,
} from '../../selectors/converterSelector';
import { breakpoints, getBreakpoint } from '../../dom/_site';
import { handleAxiosErrors } from '../../_utils';
import { axiosRequestUnpaidInvoices } from '../../actions/invoiceActions';

/**
 * Available callback:
 * - applyFilterCallback(filterData) : filterData
 * - updateViewOrderParamsCallback(listParams) : listParams
 *
 * @param props
 * @returns {*}
 * @constructor
 */
function BaseInvoiceList(props) {
    let mainTableWrapper;

    const { className, heading, callback, requestPropWatcher } = props;
    const refineResultsModalKey =
        props.refineResultsModalKey || 'refine-results-invoice-list';

    // State management
    // --------------------
    const mrtState = useManualReactTableState({
        listParams: {
            sortBy: 'PaidDate',
            sortDirection: 'DESC',
        },
    });

    // Core
    const { isLoading, toggleLoading } = mrtState;
    const {
        list,
        listParams,
        getDisplayedList,
        setListDisplay,
        updateList,
    } = mrtState;
    const { getFilters, setFilters } = mrtState;
    const {
        getTotalRecords,
        setTotalRecords,
        getActivePage,
        setActivePage,
        getTotalPages,
        getPaginationPages,
    } = mrtState;
    const { handleOnTableUpdate } = mrtState;
    const listEffectWatcher = mrtState.getEffectWatcher();

    // User Input
    // const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    // const { userInput, clearUserInput, updateUserInput } = mrtState;

    // Component Related
    // --------------------
    const getRefineResultComponent = () => {
        if (props.RefineResultComponent) {
            const RefineResultComponent = props.RefineResultComponent;
            return (
                <RefineResultComponent
                    modalKey={refineResultsModalKey}
                    activeFilters={getFilters()}
                    applyFilter={applyFilter}
                />
            );
        } else {
            return (
                <BaseInvoiceListRefineResults
                    modalKey={refineResultsModalKey}
                    activeFilters={getFilters()}
                    applyFilter={applyFilter}
                />
            );
        }
    };

    // Effects
    // --------------------
    // - Main Request
    useEffect(() => {
        request();
    }, [listEffectWatcher, requestPropWatcher]);

    // Functions
    // --------------------
    /**
     * Request List
     */
    const request = () => {
        toggleLoading('list', true);

        // Callback: updateListParam()
        let updatedListParams = JSON.parse(JSON.stringify(listParams));
        if (
            typeof callback != 'undefined' &&
            typeof callback.updateListParams != 'undefined' &&
            callback.updateListParams
        ) {
            updatedListParams = callback.updateListParams(updatedListParams);
        }

        const request = props.axiosRequest || axiosRequestUnpaidInvoices;
        const graphQLPattern = props.graphQLPattern || 'readInvoices';

        return request(updatedListParams)
            .then((response) => {
                const list = convertInvoiceListingToAReadableFormat(
                    response.data.data[graphQLPattern]
                );
                const listingDisplay = convertDataToListingDisplay(
                    response.data.data[graphQLPattern],
                    'ID'
                );
                const total = getGraphQLTotalRecords(
                    response.data.data[graphQLPattern]
                );

                console.log('Base Invoice List: ', list);

                // wait for async requests
                Promise.all([
                    setListDisplay(listingDisplay),
                    updateList(list),
                    setTotalRecords(total),
                ]).then(() => {
                    toggleLoading('list', false);
                });
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleLoading('list', false);
            });
    };

    /**
     * Set filter
     *
     * @param additionalFilterData
     */
    const applyFilter = (additionalFilterData) => {
        let updatedFilterData = {
            ...getFilters(),
        };

        if (
            typeof additionalFilterData != 'undefined' &&
            additionalFilterData
        ) {
            updatedFilterData = {
                ...updatedFilterData,
                ...additionalFilterData,
            };
        }

        setFilters(updatedFilterData);
        toggleRefineResultsModal(false);
    };

    /**
     * Toggle the refine results modal
     *
     * @param value
     */
    const toggleRefineResultsModal = (value) => {
        if (value) {
            $('#' + refineResultsModalKey).modal('show');
        } else {
            $('#' + refineResultsModalKey).modal('hide');
        }
    };

    const getOrderDetailLink = (row) => {
        if (props.getOrderDetailLink) {
            return props.getOrderDetailLink(row);
        }

        return `${window.baseURL}checkout/confirmation/${row.original.OrderID}`;
    };

    /**
     * Get table props (for React Table)
     *
     * @returns {{list: {columns: (*|Array), showPagination: boolean, manual: boolean, getTrGroupProps: (function(*, *, *, *): {className: *, onRowClick: onRowClick})}}}
     */
    const getTableProps = () => {
        let tableProps = {
            list: {
                manual: true,
                // noDataText: <span>No items found. <br/>See results from our full range below.</span>,
                columns: [],
                showPagination: false,
                getTrGroupProps: (state, row, col, instance) => {
                    const { viewIndex } = row;

                    const isExpanded =
                        state.expanded &&
                        state.expanded.hasOwnProperty(viewIndex) &&
                        state.expanded[viewIndex];
                    let classes =
                        'react-table__row react-table__row--selectable';
                    classes += ` ${
                        isExpanded ? 'react-table__row--expanded' : ''
                    }`;

                    return {
                        className: classes,
                        onRowClick: (e) => {
                            if ($(window).width() >= breakpoints.md) {
                                // loadOrder(row.original.ID);
                                window.location.href = getOrderDetailLink(row);
                            }
                        },
                    };
                },
            },
        };

        if (
            typeof callback != 'undefined' &&
            typeof callback.updateTableProps != 'undefined' &&
            callback.updateTableProps
        ) {
            tableProps = callback.updateTableProps(tableProps, {
                toggleRefineResultsModal,
                getOrderDetailLink,
            });
        }

        return tableProps;
    };

    return (
        <React.Fragment>
            <div className={classnames('card card--border', className)}>
                <div className='card__header'>
                    <div className='row row--vcenter-md'>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <h2 className='heading heading--lv4'>
                                {heading || 'Your recent invoices'}
                            </h2>
                        </div>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <div className='text-box--right'>
                                {/*<RefineResultsButton*/}
                                {/*    onClick={() => toggleRefineResultsModal(true)}*/}
                                {/*/>*/}
                            </div>
                        </div>
                    </div>
                </div>
                <ReactTableWrapper
                    key={getBreakpoint()}
                    loading={isLoading.list}
                    onFetchData={handleOnTableUpdate}
                    data={getDisplayedList()}
                    reactTable={getTableProps()}
                    ref={(r) => (mainTableWrapper = r)}
                />
            </div>
            <SectionPagination
                activePage={getActivePage()}
                totalPages={getTotalPages()}
                pages={getPaginationPages()}
                onPaginationClick={(pageNum) => setActivePage(pageNum)}
            />
            {getRefineResultComponent()}
        </React.Fragment>
    );
}

export default BaseInvoiceList;
